import type React from "react";
import type { DataTableColumn } from "./DataTable.js";

/* Column width and visibility maths for DataTable: the `minWidth` floor and
   the `hideBelow` / `foldInto` container columns. Split from DataTable.tsx so
   the table file carries only the wiring. Pure and synchronous: no React
   state, so every rule here is directly unit-testable. */

/**
 * The table's own scroll-box width below which a `hideBelow` column is hidden:
 * Tailwind v4's container sizes, 384 to 768px. These are CONTAINER widths,
 * measured on the table's box, never the viewport.
 */
export type DataTableHideBelow = "sm" | "md" | "lg" | "xl" | "2xl" | "3xl";

/** The width (px) at which each `hideBelow` column starts to show. */
export const HIDE_BELOW_WIDTH: Readonly<Record<DataTableHideBelow, number>> = {
  sm: 384,
  md: 448,
  lg: 512,
  xl: 576,
  "2xl": 672,
  "3xl": 768,
};

/* Literal class strings so Tailwind sees them. The column's th and td are
   display:none until the box reaches the width, then a table cell again. */
const HIDE_BELOW_CLASS: Record<DataTableHideBelow, string> = {
  sm: "hidden @sm:table-cell",
  md: "hidden @md:table-cell",
  lg: "hidden @lg:table-cell",
  xl: "hidden @xl:table-cell",
  "2xl": "hidden @2xl:table-cell",
  "3xl": "hidden @3xl:table-cell",
};

/* The folded copy is the exact inverse: shown while its column is hidden,
   display:none once the column shows, so both copies never show at once. */
const FOLD_CLASS: Record<DataTableHideBelow, string> = {
  sm: "@sm:hidden",
  md: "@md:hidden",
  lg: "@lg:hidden",
  xl: "@xl:hidden",
  "2xl": "@2xl:hidden",
  "3xl": "@3xl:hidden",
};

type SizedColumn = Pick<
  DataTableColumn<unknown>,
  "width" | "minWidth" | "hideBelow"
>;

/** The column's hide classes, or undefined while container columns are off. */
export function hideBelowClass(
  col: SizedColumn,
  active: boolean,
): string | undefined {
  return active && col.hideBelow ? HIDE_BELOW_CLASS[col.hideBelow] : undefined;
}

/** The classes of a folded copy of a column that hides below `hideBelow`. */
export function foldClass(hideBelow: DataTableHideBelow): string {
  return FOLD_CLASS[hideBelow];
}

/**
 * The columns folded into each target column while they are hidden, keyed by
 * the target's id. Null while container columns are off or nothing folds, so
 * the plain cell renders exactly as it did. A fold needs `hideBelow` (it only
 * shows while its column is hidden) and a target that exists.
 */
export function foldedColumns<Row>(
  columns: ReadonlyArray<DataTableColumn<Row>>,
  active: boolean,
): ReadonlyMap<string, ReadonlyArray<DataTableColumn<Row>>> | null {
  if (!active) return null;
  const ids = new Set(columns.map((col) => col.id));
  const folds = new Map<string, DataTableColumn<Row>[]>();
  for (const col of columns) {
    const target = col.foldInto;
    if (!col.hideBelow || target === undefined || target === col.id) continue;
    if (!ids.has(target)) continue;
    folds.set(target, [...(folds.get(target) ?? []), col]);
  }
  return folds.size > 0 ? folds : null;
}

/**
 * The table's min-width under `layout="fixed"`, or undefined when no column
 * sets a `minWidth` (today's equal split, no style). Numeric widths count in
 * full; the n columns without a width split what is left equally, so each
 * needs the largest floor among them; a column with a string width counts its
 * own floor. `leading` is a fixed column ahead of the data columns (the 40px
 * selection column). The box then scrolls sideways instead of any column
 * shrinking below its floor.
 */
export function fixedTableMinWidth(
  columns: ReadonlyArray<SizedColumn>,
  leading = 0,
): number | undefined {
  if (!columns.some((col) => col.minWidth !== undefined)) return undefined;
  let fixed = leading;
  const shared: number[] = [];
  for (const col of columns) {
    // Mirrors the header's `col.width ? { width } : undefined`: an empty
    // string or 0 is no width at all.
    if (!col.width) shared.push(col.minWidth ?? 0);
    else if (typeof col.width === "number") fixed += col.width;
    else fixed += col.minWidth ?? 0;
  }
  return shared.length > 0
    ? fixed + shared.length * Math.max(...shared)
    : fixed;
}

/**
 * A header cell's inline style: today's `width` when set, plus the column's
 * `minWidth` under `layout="auto"` (under `fixed` the floor is the table's
 * min-width instead). Undefined when neither applies, as today.
 */
export function columnHeaderStyle(
  col: SizedColumn,
  layout: "auto" | "fixed",
): React.CSSProperties | undefined {
  const floor = layout === "auto" ? col.minWidth : undefined;
  if (floor === undefined) return col.width ? { width: col.width } : undefined;
  return col.width
    ? { width: col.width, minWidth: floor }
    : { minWidth: floor };
}
